/**
 * Date/Time Formatter
 *
 * The only place in the app that constructs `Intl.DateTimeFormat`. Feature code
 * goes through `useDateTimeFormatter()` (`@services/datetime`) and passes a
 * preset; the raw-options signatures here exist for that hook and for tests.
 *
 * See `documents/conventions/datetime.md` for the rules these functions enforce.
 * @module @infra/format
 */

import { getLanguageLocale } from '@infra/i18n';
import type { DateFormatOptions, DatePreset, DateTimeInput, TimePreset } from './format.types';

const formattersCache = new Map<string, Intl.DateTimeFormat>();

/**
 * How each UI language writes a date, as data rather than a chain of
 * `language === 'vi' ? … : …` at each call site: Vietnamese reads dates
 * numerically (6/8/2026), English and Malay read them with an abbreviated month
 * (Aug 6, 2026 / 6 Ogo 2026). A language with no override uses `default`, which
 * is the correct behaviour for a newly added language — no call site changes.
 */
const DATE_PRESETS: Record<
  DatePreset,
  { default: DateFormatOptions; byLanguage: Record<string, DateFormatOptions> }
> = {
  full: {
    default: { month: 'short', day: 'numeric', year: 'numeric' },
    byLanguage: {
      vi: { day: 'numeric', month: 'numeric', year: 'numeric' },
    },
  },
  compact: {
    default: { month: 'short', day: 'numeric' },
    byLanguage: {
      vi: { day: 'numeric', month: 'numeric' },
    },
  },
  weekday: {
    default: { weekday: 'short' },
    byLanguage: {},
  },
  // A standalone `{ month: 'short' }` renders as "Tháng 5" in Vietnamese, which
  // overflows the 16px heatmap column; the numeric override keeps it to "5".
  monthLabel: {
    default: { month: 'short' },
    byLanguage: {
      vi: { month: 'numeric' },
    },
  },
  // The day number alone, for a narrow day picker that shows the weekday above it.
  dayOfMonth: {
    default: { day: 'numeric' },
    byLanguage: {},
  },
};

const TIME_PRESETS: Record<TimePreset, DateFormatOptions> = {
  // `hour12` left to the locale: en → "09:55 AM", vi → "09:55".
  default: { hour: '2-digit', minute: '2-digit' },
  h24: { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
  withSeconds: { hour: '2-digit', minute: '2-digit', second: '2-digit' },
};

/**
 * `Intl` options for a date preset in a given UI language.
 *
 * Returns a copy: callers naturally add a `timeZone` to what they get back, and
 * handing out the table entry itself would let one such caller re-format every
 * date in the app.
 */
export function datePresetOptions(
  language: string,
  preset: DatePreset = 'full'
): DateFormatOptions {
  const { default: fallback, byLanguage } = DATE_PRESETS[preset];
  return { ...(byLanguage[language] ?? fallback) };
}

/** `Intl` options for a time preset (copied, as above). Time layout does not vary by language. */
export function timePresetOptions(preset: TimePreset = 'default'): DateFormatOptions {
  return { ...TIME_PRESETS[preset] };
}

/**
 * Cache key must not depend on the order the caller wrote the option keys —
 * `{month,day}` and `{day,month}` describe the same format and must share one
 * formatter, so the key is built from sorted keys.
 */
function getCacheKey(locale: string | undefined, options: DateFormatOptions): string {
  return `${locale ?? ''}:${JSON.stringify(options, Object.keys(options).sort())}`;
}

function getFormatterForLocale(
  locale: string | undefined,
  options: DateFormatOptions
): Intl.DateTimeFormat {
  const cacheKey = getCacheKey(locale, options);
  let formatter = formattersCache.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    formattersCache.set(cacheKey, formatter);
  }
  return formatter;
}

function getFormatter(language: string, options: DateFormatOptions): Intl.DateTimeFormat {
  return getFormatterForLocale(getLanguageLocale(language), options);
}

/** `null`/invalid input yields no date rather than "Invalid Date" on screen. */
function toDate(value: DateTimeInput): Date | null {
  if (value === null || value === undefined) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Format an instant as a date. Invalid or missing input returns `''` — callers
 * supply their own fallback copy, this never invents one.
 */
export function formatDate(
  value: DateTimeInput,
  language: string,
  options?: DateFormatOptions
): string {
  const date = toDate(value);
  if (!date) return '';
  return getFormatter(language, options ?? datePresetOptions(language)).format(date);
}

/**
 * Format an instant in an explicitly chosen BCP-47 locale, bypassing the UI-language
 * mapping the rest of this module applies.
 *
 * For the rare caller whose locale is a deliberate choice rather than a consequence of
 * the reader's language — a date *input*, where the written form has to be unambiguous
 * (`en-GB` gives dd/mm, `en-US` gives mm/dd, and a field the user types into cannot be
 * left to guess). Prefer {@link formatDate}: it takes a language and picks the locale,
 * which is what display text should do.
 *
 * Lives here because `src/infra/format` is the one module allowed to construct
 * `Intl.DateTimeFormat`; the alternative was each such caller building its own, which
 * is the drift this module exists to prevent.
 */
export function formatDateInLocale(
  value: DateTimeInput,
  /** `undefined` leaves the choice to the runtime, as `Intl.DateTimeFormat` itself does. */
  locale: string | undefined,
  options: DateFormatOptions
): string {
  const date = toDate(value);
  if (!date) return '';
  return getFormatterForLocale(locale, options).format(date);
}

/** Format an instant as a time of day. */
export function formatTime(
  value: DateTimeInput,
  language: string,
  options?: DateFormatOptions
): string {
  const date = toDate(value);
  if (!date) return '';
  return getFormatter(language, options ?? timePresetOptions()).format(date);
}

/** Format an instant as date + time. */
export function formatDateTime(
  value: DateTimeInput,
  language: string,
  options?: DateFormatOptions
): string {
  const date = toDate(value);
  if (!date) return '';
  const effective = options ?? {
    ...datePresetOptions(language),
    ...timePresetOptions(),
  };
  return getFormatter(language, effective).format(date);
}

const MS_PER_DAY = 86_400_000;
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Format a calendar date (`YYYY-MM-DD`) — a birth date, an effective date. It is
 * NOT an instant: parsing it as one and converting to the viewer's timezone can
 * shift it a day. Parsed and formatted in UTC so the day printed is the day
 * given, everywhere on earth. Callers cannot override the timezone.
 */
export function formatDateOnly(
  value: string | null | undefined,
  language: string,
  options?: Omit<DateFormatOptions, 'timeZone'>
): string {
  if (!value) return '';
  const match = CALENDAR_DATE.exec(value);
  if (!match) return '';
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  // Round-trip rejects impossible dates like 2026-02-31, which Date.UTC rolls over.
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return '';
  }
  const effective = { ...(options ?? datePresetOptions(language)), timeZone: 'UTC' };
  return getFormatter(language, effective).format(date);
}

// Probing a zone means constructing a formatter — thousands of times slower than
// a map lookup, and resolveTimeZone runs on every render of every component that
// formats a date. The answer per zone string never changes, so probe once.
const timeZoneValidity = new Map<string, boolean>();

/** True when the runtime accepts `value` as an IANA time zone name. */
export function isValidTimeZone(value: string): boolean {
  const cached = timeZoneValidity.get(value);
  if (cached !== undefined) return cached;
  let valid = true;
  try {
    new Intl.DateTimeFormat('en', { timeZone: value });
  } catch {
    valid = false;
  }
  timeZoneValidity.set(value, valid);
  return valid;
}

/**
 * Which timezone instants are shown in: the user's saved profile timezone, else
 * the browser's, else UTC. An unrecognised zone falls through rather than
 * throwing inside a render.
 */
export function resolveTimeZone(preferred?: string | null): string {
  if (preferred && isValidTimeZone(preferred)) return preferred;
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return browserZone && isValidTimeZone(browserZone) ? browserZone : 'UTC';
}

/**
 * Whole years between a calendar date (`YYYY-MM-DD`) and today, in the local
 * calendar. `null` when the input is malformed or in the future.
 *
 * Lives here rather than beside the one screen that shows an age: it is date
 * arithmetic, and scattered date arithmetic drifts exactly like scattered
 * formatting — only harder to spot.
 */
export function calculateAge(calendarDate: string | null | undefined): number | null {
  if (!calendarDate) return null;
  const match = CALENDAR_DATE.exec(calendarDate);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const now = new Date();
  let age = now.getFullYear() - year;
  // Compare month/day as one number so "same month, earlier day" is one check.
  const hadBirthday = (now.getMonth() + 1) * 100 + now.getDate() >= month * 100 + day;
  if (!hadBirthday) age -= 1;
  return age < 0 ? null : age;
}

/**
 * Today as a calendar date (`YYYY-MM-DD`) in the user's own calendar.
 * `toISOString().slice(0, 10)` looks equivalent but reads the UTC day, so a user
 * at UTC+7 opening the app before 07:00 would get yesterday.
 */
export function todayAsCalendarDate(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

const ZONE_PARTS: DateFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
};

/** Wall-clock fields of an instant in a zone. */
function zoneFields(instant: number, timeZone: string) {
  const parts = getFormatterForLocale('en-US', { ...ZONE_PARTS, timeZone }).formatToParts(
    new Date(instant)
  );
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour') % 24,
    minute: get('minute'),
    second: get('second'),
  };
}

/** Milliseconds the zone is ahead of UTC at an instant. */
function zoneOffsetMs(instant: number, timeZone: string): number {
  const f = zoneFields(instant, timeZone);
  const wall = Date.UTC(f.year, f.month - 1, f.day, f.hour, f.minute, f.second);
  return wall - Math.floor(instant / 1000) * 1000;
}

const MS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const UTC_ZONE = 'UTC';

/** The runtime's IANA zone names; empty when it cannot list them. */
function runtimeTimeZones(): string[] {
  try {
    return typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  } catch {
    return [];
  }
}

/** Old IANA names and the names that replaced them; runtimes may list both. */
const LEGACY_TIME_ZONE_ALIASES: Readonly<Record<string, string>> = {
  'Africa/Asmera': 'Africa/Asmara',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'America/Catamarca': 'America/Argentina/Catamarca',
  'America/Cordoba': 'America/Argentina/Cordoba',
  'America/Godthab': 'America/Nuuk',
  'America/Indianapolis': 'America/Indiana/Indianapolis',
  'America/Jujuy': 'America/Argentina/Jujuy',
  'America/Louisville': 'America/Kentucky/Louisville',
  'America/Mendoza': 'America/Argentina/Mendoza',
  'Asia/Ashkhabad': 'Asia/Ashgabat',
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Chongqing': 'Asia/Shanghai',
  'Asia/Chungking': 'Asia/Shanghai',
  'Asia/Dacca': 'Asia/Dhaka',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Macao': 'Asia/Macau',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Thimbu': 'Asia/Thimphu',
  'Asia/Ujung_Pandang': 'Asia/Makassar',
  'Asia/Ulan_Bator': 'Asia/Ulaanbaatar',
  'Atlantic/Faeroe': 'Atlantic/Faroe',
  'Europe/Kiev': 'Europe/Kyiv',
  'Pacific/Enderbury': 'Pacific/Kanton',
  'Pacific/Ponape': 'Pacific/Pohnpei',
  'Pacific/Truk': 'Pacific/Chuuk',
};

/**
 * Every selectable IANA zone, de-duplicated: `common` first (in the given order), then UTC, then
 * the rest alphabetically. A legacy alias is dropped when its modern name is listed too.
 * `current` is kept even if the runtime does not list it or it is such an alias.
 */
export function listTimeZones(common: readonly string[], current?: string | null): string[] {
  const head = [...new Set([...common, UTC_ZONE])];
  const seen = new Set(head);
  const rest = [...runtimeTimeZones(), ...(current ? [current] : [])]
    .filter((zone) => !seen.has(zone) && seen.add(zone))
    .sort();
  const all = [...head, ...rest];
  const listed = new Set(all);
  return all.filter((zone) => {
    const modern = LEGACY_TIME_ZONE_ALIASES[zone];
    return zone === current || modern === undefined || !listed.has(modern);
  });
}

/** Zone name with its offset at an instant, e.g. `Asia/Kolkata (GMT+5:30)`. */
export function timeZoneLabel(timeZone: string, at: Date = new Date()): string {
  const minutes = Math.round(zoneOffsetMs(at.getTime(), timeZone) / MS_PER_MINUTE);
  const sign = minutes < 0 ? '-' : '+';
  const hours = Math.floor(Math.abs(minutes) / MINUTES_PER_HOUR);
  const rest = Math.abs(minutes) % MINUTES_PER_HOUR;
  const offset = rest ? `${hours}:${String(rest).padStart(2, '0')}` : String(hours);
  return `${timeZone} (GMT${sign}${offset})`;
}

/** The browser's zone, or null when the runtime cannot resolve a valid one. */
export function deviceTimeZone(): string | null {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return zone && isValidTimeZone(zone) ? zone : null;
}

/**
 * The last millisecond of a calendar date (`YYYY-MM-DD`) in the given zone, as an ISO-8601 UTC
 * instant. Empty string for a malformed date.
 */
export function endOfCalendarDayInstant(date: string, timeZone: string): string {
  const match = CALENDAR_DATE.exec(date);
  if (!match) return '';
  const nextMidnightAsUtc = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + 1);
  // Two passes settle the offset even when it changes between the guess and the answer.
  let instant = nextMidnightAsUtc - zoneOffsetMs(nextMidnightAsUtc, timeZone);
  instant = nextMidnightAsUtc - zoneOffsetMs(instant, timeZone);
  return new Date(instant - 1).toISOString();
}

/** The UTC day of an instant as a calendar date (`YYYY-MM-DD`), for UTC-bucketed data. */
export function utcCalendarDate(instant: Date): string {
  const month = String(instant.getUTCMonth() + 1).padStart(2, '0');
  const day = String(instant.getUTCDate()).padStart(2, '0');
  const year = String(instant.getUTCFullYear()).padStart(4, '0');
  return `${year}-${month}-${day}`;
}

/** The calendar date before `date` (`YYYY-MM-DD`), e.g. an inclusive last day from an exclusive end. */
export function previousCalendarDate(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  return utcCalendarDate(new Date(Date.UTC(year, month - 1, day - 1)));
}

/** `date` (`YYYY-MM-DD`) moved by `days` calendar days; negative goes back. */
export function shiftCalendarDate(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number);
  // setUTCFullYear: Date.UTC remaps years 0-99 to 1900-1999.
  const shifted = new Date(0);
  shifted.setUTCFullYear(year, month - 1, day + days);
  return utcCalendarDate(shifted);
}

/** Whole calendar days from `from` to `to` (`YYYY-MM-DD`); negative when `to` is earlier, NaN if malformed. */
export function calendarDaysBetween(from: string, to: string): number {
  const parse = (date: string) =>
    CALENDAR_DATE.test(date) ? Date.parse(`${date}T00:00:00Z`) : Number.NaN;
  return Math.round((parse(to) - parse(from)) / MS_PER_DAY);
}

/** First day of the month that `date` (`YYYY-MM-DD`) falls in. */
export function calendarMonthStart(date: string): string {
  const [year, month] = date.split('-');
  return `${year}-${month}-01`;
}

/**
 * The calendar date an instant falls on in `timeZone`.
 * Browser-local `todayAsCalendarDate` is wrong when the profile zone differs.
 */
export function calendarDateInTimeZone(timeZone: string, instant = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const year = parts.find((part) => part.type === 'year')?.value ?? '1970';
  const month = parts.find((part) => part.type === 'month')?.value ?? '01';
  const day = parts.find((part) => part.type === 'day')?.value ?? '01';
  return `${year}-${month}-${day}`;
}

/** Test-only handle on the formatter cache; deliberately not re-exported by the barrel. */
export const __dateTimeTest = {
  clearCache: () => formattersCache.clear(),
  cacheSize: () => formattersCache.size,
};

/**
 * The date exactly `years` before today, as `YYYY-MM-DD` on the viewer's own calendar.
 *
 * Built from local date parts, so a leap-day anniversary rolls the way the calendar does
 * (29 Feb minus 18 years lands on 1 March in a non-leap year), matching the server.
 */
export function isoDateYearsAgo(years: number): string {
  const now = new Date();
  const cutoff = new Date(now.getFullYear() - years, now.getMonth(), now.getDate());
  const month = String(cutoff.getMonth() + 1).padStart(2, '0');
  const day = String(cutoff.getDate()).padStart(2, '0');
  return `${cutoff.getFullYear()}-${month}-${day}`;
}
